import { Component, computed, effect, signal } from '@angular/core'
import { MapGeoJSONFeature } from 'maplibre-gl'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { Layers } from 'src/app/shared/layers-config/layers'
import { DetailsModel } from 'src/app/shared/models/details.model'
import { format } from 'date-fns'
import { RawDataModel } from 'src/app/shared/models/raw-event.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AppPaths } from 'src/app/app-routing.module'
import { get } from 'lodash'
import { getTimeZoneLabel } from 'src/app/shared/utils/date.utils'

@Component({
  selector: 'app-obstacle-raw-details',
  templateUrl: './obstacle-raw-details.component.html',
  standalone: false,
})
export class ObstacleRawDetailsComponent {
  private readonly _details = signal<Map<string, DetailsModel>>(new Map<string, DetailsModel>())
  public readonly details = this._details.asReadonly()
  public hasDetails = computed(() => this.details().size > 0)
  public rawFeature: string | undefined
  public features: MapGeoJSONFeature[] = []
  public zoomLevel: number | undefined
  public index: number = 0
  public globalId = ''

  constructor(
    private readonly mapEventService: MapEventsService,
    private appState: AppStateService,
  ) {
    effect(() => {
      const features = this.mapEventService.clickedFeatures()
      this._details.set(new Map<string, DetailsModel>())
      this.features.length != features.length ? (this.index = 0) : this.index
      if (
        features[this.index]?.layer?.id === Layers.Provider1Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider1Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider1ConstructionSites.name ||
        features[this.index]?.layer?.id === Layers.Provider2Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider2Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider2ConstructionSites.name ||
        features[this.index]?.layer?.id === Layers.Provider3Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider3Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider3ConstructionSites.name ||
        features[this.index]?.layer?.id === Layers.Provider4Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider4Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider6Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider6Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider7Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider7Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider8Accidents.name ||
        features[this.index]?.layer?.id === Layers.Provider8Bdv.name ||
        features[this.index]?.layer?.id === Layers.Provider8Wdw.name ||
        features[this.index]?.layer?.id === Layers.Provider8Sv.name ||
        features[this.index]?.layer?.id === Layers.Provider8Cs.name ||
        features[this.index]?.layer?.id === Layers.Provider9Cs.name ||
        features[this.index]?.layer?.id === Layers.Provider9Sv.name
      ) {
        this.features = features
        this.setCurrentFeature(this.index)
      }
    })
  }

  toggleFeature(index: number) {
    this.index = index
    this.setCurrentFeature(index)
  }

  setCurrentFeature(index: number = this.index) {
    this.index = index
    this.globalId = this.features[this.index].id?.toString() ?? ''
    this.rawFeature = JSON.stringify(this.features[this.index], undefined, 2)
    const properties: RawDataModel = this.features[this.index].properties as RawDataModel
    this.zoomLevel = this.appState.getState().zoom
    this.mapObstaclesRawToDetails(properties)
    this.appState.setState(AppPaths.Obstacle, {
      selectedFeatureId: properties.id,
    })
  }

  mapObstaclesRawToDetails(properties: RawDataModel) {
    this.setDetail('RawMetric', {
      title: 'Hazards',
      items: [
        {
          description: 'ID',
          value: properties.id?.toString() ?? '',
        },
        {
          description: 'Type',
          value: properties.hazardType!,
        },
        {
          description: `Valid time (${getTimeZoneLabel(new Date(properties.startTimestamp))})`,
          value: `${
            properties.startTimestamp
              ? format(new Date(properties.startTimestamp), 'yyyy-MM-dd HH:mm')
              : '-'
          } - ${
            properties.endTimestamp
              ? format(new Date(properties.endTimestamp), 'yyyy-MM-dd HH:mm')
              : '-'
          }`,
        },
        {
          description: 'isCancelled',
          value: properties.isCancelled?.toString() ?? 'n/a',
        },
      ],
    })
  }

  private setDetail(key: string, value: DetailsModel): void {
    this._details.update((current) => {
      const next = new Map(current)
      next.set(key, value)
      return next
    })
  }

  close() {
    this._details.set(new Map<string, DetailsModel>())
    this.appState.setState(AppPaths.Obstacle, {
      selectedFeatureId: undefined,
    })
  }
}
